import { FlaskIcon } from "@phosphor-icons/react/dist/csr/Flask";
import { useEffect, useRef } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { getPayrollRosterApiErrorMessage } from "@/payroll/roster/api-errors";
import { AccessComparison } from "@/payroll/workbook-access/access-comparison";
import { usePreviewWorkbookAccess } from "@/payroll/workbook-access/query";
import type { WorkingAccess } from "@/payroll/workbook-access/types";

interface PersonAccessCheckProps {
  businessId: string;
  userId: string;
  // This drawer's choices on top of live access. Apply is what saves them.
  candidate: WorkingAccess;
}

export const PersonAccessCheck = ({
  businessId,
  userId,
  candidate,
}: PersonAccessCheckProps) => {
  const test = usePreviewWorkbookAccess(businessId);
  const candidateKey = JSON.stringify(candidate);
  const { reset } = test;
  // A result for different choices would mislead: clear it when they change.
  useEffect(() => reset(), [candidateKey, reset]);
  // A request still in flight for earlier choices must not show its answer.
  const current = JSON.stringify(test.variables) === candidateKey;
  const result = current ? test.data : undefined;
  const person = result?.people.find((p) => p.user_id === userId);
  const resultRef = useRef<HTMLDivElement>(null);
  // The result lands below the fold of a long drawer: bring it into view.
  useEffect(() => {
    if (result) resultRef.current?.scrollIntoView({ block: "nearest" });
  }, [result]);

  return (
    <Stack
      align="start"
      ref={resultRef}
      gap={2}
      className="w-full rounded-xl bg-neutral p-4"
    >
      <Stack
        className="w-full"
        gap={4}
        direction="row"
        align="center"
        justify="between"
      >
        <Text variant="label">What they will see</Text>
        <Button
          variant="outline"
          size="sm"
          disabled={test.isPending}
          aria-busy={test.isPending || undefined}
          onClick={() => test.mutate(candidate)}
        >
          {test.isPending ? (
            <Spinner aria-hidden data-icon="inline-start" />
          ) : (
            <FlaskIcon data-icon="inline-start" className="h-4 w-4" />
          )}
          {result ? "Test again" : "Test access"}
        </Button>
      </Stack>
      {test.isError && (
        <Alert color="danger">
          {getPayrollRosterApiErrorMessage(test.error)}
        </Alert>
      )}
      {result && !result.valid && (
        <Alert color="danger">{result.errors.join(". ")}</Alert>
      )}
      {person ? (
        <AccessComparison person={person} />
      ) : (
        !test.isPending && (
          <Text variant="caption" tone="muted">
            Check the tabs and technicians this person would get, using the
            latest workbook. Nothing is saved.
          </Text>
        )
      )}
    </Stack>
  );
};
